import {
	Checkbox,
	DatePicker,
	FormItem,
	RadioButtonGroup,
	RadioButtonGroupItem,
	ValidationError
} from "client/Components";
import { TagKind } from "client/Api/Tags/tags";
import { IObjectInputAttachable } from "client/Forms/attachable";
import { ReactElement } from "react";
import { localize } from "client_legacy/ndaw/culture";
import { HubItemComposerDialog } from "../../HubItemComposerDialog/HubItemComposerDialog";
import {
	EventSaveModel,
	EventStatus
} from "client/Api/EventsManagement/eventsManagement";
import { HubTagSelect } from "client/Features/Hub/Builder/HubTagSelect";
import { TimeZone } from "client/DateAndTime/timezones";
import { HubHierarchyListNodeModel } from "client/Api/Hub/common.composer";

const {
	Title,
	Step,
	CategorySelect,
	Content,
	FormContainer
} = HubItemComposerDialog;

export type SettingsStepProps = {
	isActive: boolean;
	attachable: IObjectInputAttachable<EventSaveModel>;
	status: EventStatus;
	categoryHierarchy: readonly HubHierarchyListNodeModel[];
	hasPermissionToCreateNewCategory: boolean;
	hasPermissionToSendDirectPopups: boolean;
	onCreateNewCategory(): Promise<void>;
	timezone: TimeZone;
	hasPublicationDate: boolean;
	onHasPublicationDateChange(value: boolean): void;
	infoAlerts?: ReactElement;
};

export const SettingsStep = (props: SettingsStepProps): ReactElement => {
	const {
		isActive,
		attachable,
		status,
		categoryHierarchy,
		onCreateNewCategory,
		hasPermissionToCreateNewCategory,
		hasPermissionToSendDirectPopups,
		timezone,
		hasPublicationDate,
		onHasPublicationDateChange,
		infoAlerts
	} = props;

	const isArchived: boolean = status === EventStatus.Archived;
	const isPublished: boolean = status === EventStatus.Published;
	const isDirectPopUpDisabled: boolean = isPublished
		|| isArchived
		|| !hasPermissionToSendDirectPopups;

	return (
		<Step
			isActive={isActive}
			trackingId="settings-step"
		>
			<Content>
				{infoAlerts}
				<FormContainer>
					<Title text={localize("_Settings")} />
					<FormItem
						label={localize("_Category")}
						isRequired
					>
						<CategorySelect
							attachable={attachable.for("categoryId")}
							categoryHierarchy={categoryHierarchy}
							hasPermissionToCreateNewCategory={hasPermissionToCreateNewCategory}
							isArchived={isArchived}
							onCreateNewCategory={onCreateNewCategory}
							menuTitle={localize("_Category")}
						/>
						<ValidationError {...attachable.for("categoryId").attachToValidationError()} />
					</FormItem>
					<FormItem label={localize("_Tags")}>
						<HubTagSelect
							attachable={attachable.for("tags")}
							kind={TagKind.Event}
							isDisabled={isArchived}
							menuTitle={localize("_Tags")}
						/>

						<ValidationError {...attachable.for("tags").attachToValidationError()} />
					</FormItem>
					<FormItem label={localize("_DirectPopup")}>
						<Checkbox
							{...attachable.for("showAutomatically").attachToCheckbox()}
							text={localize("_ShowPopupDirectlyToUser")}
							isDisabled={isDirectPopUpDisabled}
						/>
						<ValidationError {...attachable.for("showAutomatically").attachToValidationError()} />
					</FormItem>

					<FormItem label={localize("_Author")}>
						<Checkbox
							{...attachable.for("shouldShowAuthor").attachToCheckbox()}
							text={localize("_ShowAuthorOnEvent")}
							isDisabled={isArchived}
						/>
						<ValidationError {...attachable.for("shouldShowAuthor").attachToValidationError()} />
					</FormItem>

					<FormItem label={localize("_Comments")}>
						<Checkbox
							{...attachable.for("areCommentsEnabled").attachToCheckbox()}
							text={localize("_AllowComments")}
							isDisabled={isArchived}
						/>
						<ValidationError {...attachable.for("areCommentsEnabled").attachToValidationError()} />
					</FormItem>
					<FormItem
						label={localize("_PublicationStart")}
						isForGroup
					>
						<RadioButtonGroup
							value={hasPublicationDate}
							onChange={onHasPublicationDateChange}
							options={publicationStartOptions}
							isDisabled={isArchived || isPublished}
							trackingId="publication-start"
						/>
					</FormItem>
					{hasPublicationDate && (
						<FormItem label={localize("_SetStartDate")} isRequired>
							<DatePicker
								{...attachable.for("publicationDate").attachToDatePicker()}
								isDisabled={isArchived || isPublished}
								timezone={timezone}
							/>
							<ValidationError {...attachable.for("publicationDate").attachToValidationError()} />
						</FormItem>
					)}
				</FormContainer>
			</Content>
		</Step>
	);
};
SettingsStep.displayName = "SettingsStep";

const publicationStartOptions: readonly RadioButtonGroupItem<boolean>[] = [
	{
		value: false,
		text: localize("_PublishNow")
	},
	{
		value: true,
		text: localize("_SetStartDate")
	}
];